import {useCallback, useEffect, useState} from "react";
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress,
    Pagination, Paper, TextField, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useUser} from "../../providers/user-context";
import {eventBus} from "../../event-bus";
import {PaginationContainer} from "../../components/common/page-container";
import ProductCatalogueRows from "./product-catalogue-rows";
import ProductImport from "./product-import";
import useProductCatalogue from "./use-product-catalogue";
import useProductEditor from "./use-product-editor";
import {productDraft} from "./product-catalogue-api";

function CatalogueSearch({catalogue, disabled, t}) {
    const [search, setSearch] = useState("");
    const [invalid, setInvalid] = useState(false);
    const apply = event => {
        event.preventDefault();
        const value = search.trim();
        if (value && value.length < 3) {setInvalid(true); return;}
        setInvalid(false); catalogue.setQuery({search: value, page: 0});
    };
    return <Box component="form" onSubmit={apply} sx={{display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2, alignItems: "flex-start"}}>
        <TextField label={t("ProductsSearchLabel")} size="small" value={search} disabled={disabled} error={invalid}
            helperText={t("ProductsSearchHint")} sx={{flex: "1 1 260px"}}
            onChange={event => {setSearch(event.target.value); setInvalid(false);}}/>
        <Button type="submit" variant="outlined" disabled={disabled}>{t("ProductsSearchAction")}</Button>
        <Button disabled={disabled || (!search && !catalogue.query.search)} onClick={() => {
            setSearch(""); setInvalid(false); catalogue.setQuery({search: "", page: 0});
        }}>{t("Reset")}</Button>
    </Box>;
}

function ProductsPanel({uploadRequest, onWorkflowOpened, onEditStateChange}) {
    const {t, i18n} = useTranslation();
    const catalogue = useProductCatalogue(i18n.language);
    const [removing, setRemoving] = useState(null);
    const [importBusy, setImportBusy] = useState(false);
    const editor = useProductEditor(i18n.language, catalogue.updateRow, catalogue.reload);
    const busy = editor.busy || importBusy;
    const locked = busy || !!editor.editing;
    const dirty = !!editor.editing && JSON.stringify(editor.draft) !== JSON.stringify(productDraft(editor.editing));
    const discard = useCallback(() => editor.cancel(), [editor.cancel]);
    useEffect(() => {
        if (uploadRequest && editor.editing && !dirty && !busy) editor.cancel();
    }, [uploadRequest, editor.editing, dirty, busy, editor.cancel]);
    useEffect(() => {onEditStateChange?.({dirty, saving: busy, discard});},
        [dirty, busy, discard, onEditStateChange]);
    useEffect(() => () => onEditStateChange?.(null), [onEditStateChange]);
    useEffect(() => {
        const refresh = () => {if (!locked) catalogue.reload();};
        eventBus.on("refreshProductsData", refresh);
        return () => eventBus.off("refreshProductsData", refresh);
    }, [locked, catalogue.reload]);
    const total = catalogue.status === "success" && catalogue.total !== null
        ? new Intl.NumberFormat(i18n.language || "en").format(catalogue.total) : "—";
    return <Box>
        <Box sx={{display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "space-between", alignItems: "center", mb: 2}}>
            <Typography variant="h6">{t("Products")} ({total})</Typography>
            <Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
                <Button disabled={locked || catalogue.status === "loading"} onClick={catalogue.reload}>{t("Refresh")}</Button>
                <ProductImport uploadRequest={uploadRequest} onWorkflowOpened={onWorkflowOpened} disabled={locked}
                    onImported={catalogue.reload} onBusyChange={setImportBusy}
                    onError={message => editor.setNotice({severity: "error", message})}/>
            </Box>
        </Box>
        {editor.notice && !removing && <Alert severity={editor.notice.severity} sx={{mb: 2}}
            role={editor.notice.severity === "success" ? "status" : "alert"}>{t(editor.notice.message)} {editor.notice.product}</Alert>}
        {catalogue.status === "loading" && <LinearProgress aria-label={t("Loading")} sx={{mb: 2}}/>}
        <Paper variant="outlined" sx={{p: {xs: 1.5, sm: 2.5}, borderRadius: 2, minWidth: 0}}>
            <CatalogueSearch catalogue={catalogue} disabled={locked} t={t}/>
            <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 2}}>{t("ProductsFiltersUnavailable")}</Typography>
            {catalogue.status === "error" ? <Alert severity="error" action={<Button onClick={catalogue.reload}>{t("Retry")}</Button>}>
                {t(catalogue.error)}</Alert> : catalogue.status === "success" && <>
                {catalogue.total === null && <Typography role="status" sx={{mb: 2}}>{t("ProductsTotalUnavailable")}</Typography>}
                {catalogue.rows.length ? <ProductCatalogueRows rows={catalogue.rows} editor={editor}
                    onRemove={setRemoving} t={t} language={i18n.language}/>
                    : <Typography role="status" sx={{py: 4, textAlign: "center"}}>
                        {t(catalogue.query.search ? "ProductsNoMatches" : "ProductsEmpty")}</Typography>}
                {catalogue.pages > 1 && <PaginationContainer><Pagination count={catalogue.pages} page={catalogue.query.page + 1}
                    disabled={locked} onChange={(_, page) => catalogue.setQuery(previous => ({...previous, page: page - 1}))}
                    color="primary" shape="rounded"/></PaginationContainer>}
            </>}
        </Paper>
        <Dialog open={!!removing} onClose={() => {if (!editor.busy) setRemoving(null);}} aria-labelledby="product-remove-title">
            <DialogTitle id="product-remove-title">{t("ProductsRemoveTitle")}</DialogTitle>
            <DialogContent><Typography sx={{overflowWrap: "anywhere"}}>{removing?.productName} · {removing?.productCode}</Typography>
                {editor.notice?.severity === "error" && <Alert severity="error">{t(editor.notice.message)}</Alert>}</DialogContent>
            <DialogActions><Button disabled={editor.busy} onClick={() => setRemoving(null)}>{t("Cancel")}</Button>
                <Button color="error" disabled={editor.busy} onClick={async () => {
                    if (await editor.remove(removing)) setRemoving(null);
                }}>{t(editor.busy ? "ProductsSaving" : "Remove")}</Button></DialogActions>
        </Dialog>
    </Box>;
}

export default function TabOurProducts({tabValue, uploadRequest = 0, onWorkflowOpened, onEditStateChange}) {
    const {tenantId, userIdentity} = useUser();
    const {i18n} = useTranslation();
    return <div role="tabpanel" hidden={tabValue !== 2} id="distributor-tabpanel-2" aria-labelledby="distributor-tab-2">
        {tabValue === 2 && <ProductsPanel key={`${tenantId}:${userIdentity}:${i18n.language}`}
            uploadRequest={uploadRequest} onWorkflowOpened={onWorkflowOpened} onEditStateChange={onEditStateChange}/>}
    </div>;
}
